import React, { useEffect, useState, useCallback } from 'react'
import { RouteComponentProps } from 'react-router'
import Modal from '../../components/Modal'
import CycleCountStatus from './CycleCountStatus'
import CycleCountApproveTable from './CycleCountApproveTable'
import useTitle from 'react-use/lib/useTitle'
import CycleCountEditFooter from './CycleCountEditFooter'
import { useSelector } from 'react-redux'
import useThunkDispatch from '../../common/useThunkDispatch'
import {
  fetchCycleCount,
  updateCycleCountItems,
  updateCycleCount,
} from '../cycleCountsActions'
import { currentCycleCountSelector } from '../cycleCountsSelectors'
import { ICycleCount, ICycleCountItem } from '../../types/cycle-counts'

interface ICycleCountEditProps {
  loading: boolean
}

const CycleCountEdit: React.FC<RouteComponentProps & ICycleCountEditProps> = ({
  match,
  history,
  loading,
}) => {
  const { cycleCountId, accountId, branchId } = match.params as {
    cycleCountId: string
    accountId?: string
    branchId?: string
  }
  const dispatch = useThunkDispatch()

  const localCycleCount = useSelector(
    currentCycleCountSelector,
  ) as ICycleCount | null

  const locationTypePrefix = localCycleCount?.location?.locationType
    ? localCycleCount.location.locationType.toUpperCase()
    : 'COUNT'

  useTitle(
    `${locationTypePrefix}${localCycleCount?.location?.code ? ` · ${localCycleCount.location.code}` : ''}`,
  )

  const [loadingQuery, setLoadingQuery] = useState<boolean>(false)
  const [loadingMutation, setLoadingMutation] = useState<boolean>(false)
  const [submitting, setSubmitting] = useState<boolean>(false)
  const [selectedItems, setSelectedItems] = useState<ICycleCountItem[]>([])

  useEffect(() => {
    const loadCycleCount = async () => {
      setLoadingQuery(true)
      try {
        await dispatch(fetchCycleCount(Number(cycleCountId)))
      } catch (e) {
        console.error('Error loading cycle count:', e)
      }
      setLoadingQuery(false)
    }
    loadCycleCount()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cycleCountId])

  const onClose = useCallback(() => {
    const state = history.location.state
    if (accountId && branchId) {
      history.push(
        `/clients/${accountId}/branches/${branchId}/cycle_counts`,
        state,
      )
    } else {
      history.push('/cycle_counts', state)
    }
  }, [history, accountId, branchId])

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose()
      }
    }

    window.addEventListener('keydown', handleKeyDown)

    return () => {
      window.removeEventListener('keydown', handleKeyDown)
    }
  }, [onClose])

  const handleItemSelect = (item: ICycleCountItem) => {
    if (item.status === 'approved') return

    if (selectedItems.includes(item)) {
      setSelectedItems((prev) => prev.filter((i) => i !== item))
    } else {
      setSelectedItems((prev) => [...prev, item])
    }
  }

  const toggleAllItems = () => {
    const selectableItems =
      (localCycleCount?.cycleCountItems as ICycleCountItem[])?.filter(
        (item) => item.status !== 'approved',
      ) || []

    const allSelectableSelected =
      selectableItems.length > 0 &&
      selectableItems.every((item) => selectedItems.includes(item))

    if (allSelectableSelected) {
      setSelectedItems([])
    } else {
      setSelectedItems(selectableItems)
    }
  }

  const updateItemsStatus = async (status: 'approved' | 'rejected') => {
    if (!selectedItems.length) return

    setLoadingMutation(true)

    try {
      const errors = await dispatch(
        updateCycleCountItems(
          Number(cycleCountId),
          selectedItems.map((item) => item.id),
          status,
        ),
      )

      if (errors) {
        console.error('Failed to update cycle count items:', errors)
        return
      }

      setSelectedItems([])
    } catch (e) {
      console.error('Error updating items status:', e)
    } finally {
      setLoadingMutation(false)
    }
  }

  const onRecount = () => updateItemsStatus('rejected')
  const onApprove = () => updateItemsStatus('approved')
  const onComplete = () => {
    if (!localCycleCount) return

    setSubmitting(true)

    dispatch(updateCycleCount(Number(cycleCountId), { status: 'completed' }))
      .then((errors) => {
        if (errors) {
          console.error('Failed to complete cycle count:', errors)
          return
        }
      })
      .catch((e) => {
        console.error('Error completing cycle count:', e)
      })
      .finally(() => {
        setSubmitting(false)
        onClose()
      })
  }
  const onSubmit = () => {
    if (!localCycleCount) return

    const items = (localCycleCount.cycleCountItems || []) as ICycleCountItem[]
    const hasRejected = items.some(
      (item: ICycleCountItem) => item.status === 'rejected',
    )

    if (hasRejected) {
      setSubmitting(true)

      dispatch(updateCycleCount(Number(cycleCountId), { status: 'rejected' }))
        .then((errors) => {
          if (errors) {
            console.error('Failed to update cycle count:', errors)
            return
          }
        })
        .catch((e) => {
          console.error('Error updating cycle count:', e)
        })
        .finally(() => {
          setSubmitting(false)
          onClose()
        })
    }
  }

  return (
    <Modal
      onClose={() => onClose()}
      title={`${locationTypePrefix}${localCycleCount?.location?.code ? ` · ${localCycleCount.location.code}` : ''}`}
    >
      <div className="cycle-count">
        <div className="cycle-count_content">
          {localCycleCount && <CycleCountStatus cycleCount={localCycleCount} />}
          <CycleCountApproveTable
            itemList={localCycleCount?.cycleCountItems || []}
            loading={loading || loadingQuery}
            selectedItems={selectedItems}
            onItemSelect={handleItemSelect}
            toggleAllItems={toggleAllItems}
            isCompleted={localCycleCount?.status === 'completed'}
          />
        </div>
        {localCycleCount?.status !== 'completed' && (
          <CycleCountEditFooter
            cycleCountItems={localCycleCount?.cycleCountItems || []}
            loading={loading || loadingMutation || submitting}
            onSubmit={onSubmit}
            onClose={onClose}
            onComplete={onComplete}
            onRecount={onRecount}
            onApprove={onApprove}
            selectedItems={selectedItems}
          />
        )}
      </div>
    </Modal>
  )
}

export default CycleCountEdit
